





































import Vue, { PropType } from 'vue';
import { OrgPeerGroup } from '@/models/orgStructure';
import * as Utils from '@/utils';

import LazyRenderingList from '@/components/LazyRenderingList.vue';
import PeerGroupCard from '@/components/PeerGroupCard.vue';
import PeerGroupListCreator from '@/components/PeerGroupListCreator.vue';

import {
  DEFAULT_PEER_GROUP_SORT,
  PeerGroupSortField,
  loadPeerGroupSort,
  savePeerGroupSort
} from '@/preferences';

// Matching the pill colors used by RoleSelectorPills.vue
const ACTIVE_PILL_CLASSES = ['orange', 'lighten-4'];
const INACTIVE_PILL_CLASSES = ['grey', 'lighten-4'];

export default Vue.extend({
  components: {
    LazyRenderingList,
    PeerGroupCard,
    PeerGroupListCreator
  },

  props: {
    filter: String,
    peerGroups: Array as PropType<OrgPeerGroup[]>,
    createEnabled: Boolean,
    sortEnabled: Boolean
  },

  data() {
    // Only a list that shows the control follows the saved preference; the
    // others give the viewer no way to see or change it.
    const sort = this.sortEnabled
      ? loadPeerGroupSort()
      : DEFAULT_PEER_GROUP_SORT;

    return {
      sortField: sort.field,
      sortDescending: sort.descending
    };
  },

  computed: {
    sortOptions(): {
      field: PeerGroupSortField;
      label: string;
      classes: string[];
    }[] {
      const options: { field: PeerGroupSortField; label: string }[] = [
        { field: 'name', label: 'Name' },
        { field: 'createdAt', label: 'Created' }
      ];

      return options.map(option => ({
        ...option,
        classes:
          this.sortField === option.field
            ? ACTIVE_PILL_CLASSES
            : INACTIVE_PILL_CLASSES
      }));
    },
    directionIcon(): string {
      return this.sortDescending ? 'mdi-arrow-down' : 'mdi-arrow-up';
    },
    comparator(): (a: OrgPeerGroup, b: OrgPeerGroup) => number {
      const descending = this.sortDescending;

      if (this.sortField === 'createdAt') {
        return (a, b) => Utils.sortByDate(a, b, pg => pg.createdAt, descending);
      }

      return (a, b) => Utils.sortNamed(a, b, descending);
    },
    // Archived groups always follow the active ones, sorted within their block.
    sorted(): OrgPeerGroup[] {
      const active = this.peerGroups
        .filter(pg => pg.isActive)
        .sort(this.comparator);
      const archived = this.peerGroups
        .filter(pg => !pg.isActive)
        .sort(this.comparator);

      return [...active, ...archived];
    },
    filtered(): OrgPeerGroup[] {
      if (!this.filter) {
        return this.sorted;
      }

      return [
        ...Utils.findTerm(this.filter, this.sorted, pg => [
          pg.name,
          pg.section,
          pg.description
        ])
      ];
    },
    hasResults(): boolean {
      return this.filtered.length > 0;
    },
    showCreateForm(): boolean {
      return this.createEnabled && !this.hasResults && !!this.filter;
    }
  },

  methods: {
    selectSort(field: PeerGroupSortField) {
      // Reselecting the active field flips it; a new field starts at its own
      // default, since names read best A-Z and dates read best newest first.
      const descending =
        field === this.sortField ? !this.sortDescending : field === 'createdAt';

      this.sortField = field;
      this.sortDescending = descending;

      savePeerGroupSort({ field, descending });
    },
    onCreate(group: { name: string; section: string; description: string }) {
      this.$emit('create-group', group);
    }
  },

  watch: {
    filtered(current, prev) {
      if (current.length != prev.length) {
        this.$emit('filter', { count: current.length });
      }
    }
  }
});
